// src/pages/CaseManagement.js

import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { X } from "lucide-react";

import {
    getInitialCaseState,
    initialQuestionState,
} from "../data/initialStates";
import { DifficultyLevel, Industry, CaseType } from "../enums";

import CaseKeyPoints from "./CaseKeyPoints";
import CaseQuestions from "./CaseQuestions";
import CaseList from "./CaseList";

const CaseManagement = () => {
    const [cases, setCases] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [currentCaseId, setCurrentCaseId] = useState(null);

    // Form data for adding/editing a single case
    const [formData, setFormData] = useState(getInitialCaseState());

    /* -------------------------------------------------------------------------
     * CASE-LEVEL FIELD HANDLERS
     * ------------------------------------------------------------------------*/
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleInterviewerLedChange = (checked) => {
        setFormData((prev) => ({ ...prev, isInterviewerLed: checked }));
    };

    const handleIndustrySelect = (industry) => {
        setFormData((prev) => ({ ...prev, industry }));
    };

    const handleCaseTypeSelect = (caseType) => {
        setFormData((prev) => ({ ...prev, caseType }));
    };

    // Tags
    const handleTagInput = (e) => {
        if (e.key === "Enter" && e.target.value) {
            const newTag = e.target.value.trim();
            if (newTag) {
                setFormData((prev) => ({
                    ...prev,
                    tags: [...prev.tags, newTag],
                }));
            }
            e.target.value = "";
        }
    };
    const removeTag = (tagToRemove) => {
        setFormData((prev) => ({
            ...prev,
            tags: prev.tags.filter((t) => t !== tagToRemove),
        }));
    };

    // Companies
    const handleCompanyInput = (e) => {
        if (e.key === "Enter" && e.target.value) {
            const newCompany = e.target.value.trim();
            if (newCompany) {
                setFormData((prev) => ({
                    ...prev,
                    companies: [...prev.companies, newCompany],
                }));
            }
            e.target.value = "";
        }
    };
    const removeCompany = (companyToRemove) => {
        setFormData((prev) => ({
            ...prev,
            companies: prev.companies.filter((c) => c !== companyToRemove),
        }));
    };

    /* -------------------------------------------------------------------------
     * CASE KEY POINTS
     * ------------------------------------------------------------------------*/
    const handleAddCaseKeyPoint = () => {
        setFormData((prev) => {
            const newId = prev.keyPoints.length
                ? prev.keyPoints[prev.keyPoints.length - 1].id + 1
                : 1;
            return {
                ...prev,
                keyPoints: [
                    ...prev.keyPoints,
                    {
                        ...prev.keyPoints[0],
                        id: newId,
                        title: "",
                        dataPoints: [],
                    },
                ],
            };
        });
    };

    const handleRemoveCaseKeyPoint = (kpIndex) => {
        setFormData((prev) => ({
            ...prev,
            keyPoints: prev.keyPoints.filter((_, i) => i !== kpIndex),
        }));
    };

    const handleCaseKeyPointChange = (kpIndex, field, value) => {
        setFormData((prev) => {
            const keyPoints = [...prev.keyPoints];
            if (field === "dataPoint") {
                if (!keyPoints[kpIndex].dataPoints) {
                    keyPoints[kpIndex].dataPoints = [];
                }
                keyPoints[kpIndex].dataPoints.push(value);
            } else {
                keyPoints[kpIndex][field] = value;
            }
            return { ...prev, keyPoints };
        });
    };

    const handleCaseDataPointInput = (kpIndex, e) => {
        if (e.key === "Enter" && e.target.value) {
            const newValue = e.target.value.trim();
            if (newValue) {
                handleCaseKeyPointChange(kpIndex, "dataPoint", newValue);
            }
            e.target.value = "";
        }
    };

    const removeCaseDataPoint = (kpIndex, dp) => {
        setFormData((prev) => {
            const keyPoints = [...prev.keyPoints];
            keyPoints[kpIndex].dataPoints = keyPoints[
                kpIndex
            ].dataPoints.filter((x) => x !== dp);
            return { ...prev, keyPoints };
        });
    };

    /* -------------------------------------------------------------------------
     * CASE QUESTIONS
     * ------------------------------------------------------------------------*/
    const handleAddQuestion = () => {
        setFormData((prev) => {
            const newQId = prev.caseQuestions.length + 1;
            return {
                ...prev,
                caseQuestions: [
                    ...prev.caseQuestions,
                    { ...initialQuestionState, questionId: newQId },
                ],
            };
        });
    };

    const handleRemoveQuestion = (qIndex) => {
        setFormData((prev) => ({
            ...prev,
            caseQuestions: prev.caseQuestions.filter((_, i) => i !== qIndex),
        }));
    };

    const handleQuestionChange = (qIndex, field, value) => {
        setFormData((prev) => {
            const questions = [...prev.caseQuestions];
            questions[qIndex] = { ...questions[qIndex], [field]: value };
            return { ...prev, caseQuestions: questions };
        });
    };

    // Basic/Additional info
    const handleQuestionBasicInfo = (qIndex, e) => {
        if (e.key === "Enter" && e.target.value) {
            const val = e.target.value.trim();
            if (val) {
                setFormData((prev) => {
                    const questions = [...prev.caseQuestions];
                    const setCopy = new Set(questions[qIndex].basicInformation);
                    setCopy.add(val);
                    questions[qIndex].basicInformation = setCopy;
                    return { ...prev, caseQuestions: questions };
                });
            }
            e.target.value = "";
        }
    };
    const removeQuestionBasicInfo = (qIndex, item) => {
        setFormData((prev) => {
            const questions = [...prev.caseQuestions];
            const setCopy = new Set(questions[qIndex].basicInformation);
            setCopy.delete(item);
            questions[qIndex].basicInformation = setCopy;
            return { ...prev, caseQuestions: questions };
        });
    };

    const handleQuestionAdditionalInfo = (qIndex, e) => {
        if (e.key === "Enter" && e.target.value) {
            const val = e.target.value.trim();
            if (val) {
                setFormData((prev) => {
                    const questions = [...prev.caseQuestions];
                    const setCopy = new Set(
                        questions[qIndex].additionalInformation
                    );
                    setCopy.add(val);
                    questions[qIndex].additionalInformation = setCopy;
                    return { ...prev, caseQuestions: questions };
                });
            }
            e.target.value = "";
        }
    };
    const removeQuestionAdditionalInfo = (qIndex, item) => {
        setFormData((prev) => {
            const questions = [...prev.caseQuestions];
            const setCopy = new Set(questions[qIndex].additionalInformation);
            setCopy.delete(item);
            questions[qIndex].additionalInformation = setCopy;
            return { ...prev, caseQuestions: questions };
        });
    };

    // Key points within question
    const handleAddQuestionKeyPoint = (qIndex) => {
        setFormData((prev) => {
            const questions = [...prev.caseQuestions];
            const newKpId = (questions[qIndex].keyPoints.length || 0) + 1;
            const newKp = { id: newKpId, title: "", dataPoints: [] };
            questions[qIndex].keyPoints = [
                ...questions[qIndex].keyPoints,
                newKp,
            ];
            return { ...prev, caseQuestions: questions };
        });
    };

    const handleRemoveQuestionKeyPoint = (qIndex, kpIndex) => {
        setFormData((prev) => {
            const questions = [...prev.caseQuestions];
            questions[qIndex].keyPoints = questions[qIndex].keyPoints.filter(
                (_, i) => i !== kpIndex
            );
            return { ...prev, caseQuestions: questions };
        });
    };

    const handleQuestionKeyPointChange = (qIndex, kpIndex, field, value) => {
        setFormData((prev) => {
            const questions = [...prev.caseQuestions];
            const kpArr = [...questions[qIndex].keyPoints];
            if (field === "dataPoint") {
                kpArr[kpIndex].dataPoints = [
                    ...(kpArr[kpIndex].dataPoints || []),
                    value,
                ];
            } else {
                kpArr[kpIndex][field] = value;
            }
            questions[qIndex].keyPoints = kpArr;
            return { ...prev, caseQuestions: questions };
        });
    };

    const handleQuestionKeyPointDataInput = (qIndex, kpIndex, e) => {
        if (e.key === "Enter" && e.target.value) {
            const val = e.target.value.trim();
            if (val) {
                handleQuestionKeyPointChange(qIndex, kpIndex, "dataPoint", val);
            }
            e.target.value = "";
        }
    };

    const removeQuestionKeyPointDataPoint = (qIndex, kpIndex, dp) => {
        setFormData((prev) => {
            const questions = [...prev.caseQuestions];
            const kpArr = [...questions[qIndex].keyPoints];
            kpArr[kpIndex].dataPoints = kpArr[kpIndex].dataPoints.filter(
                (x) => x !== dp
            );
            questions[qIndex].keyPoints = kpArr;
            return { ...prev, caseQuestions: questions };
        });
    };

    // Math question extras
    const handleCalculationKeyPointAdd = (qIndex) => {
        const keyName = prompt("Enter key/dimension:");
        const keyValue = prompt("Enter value/calculation detail:");
        if (keyName && keyValue) {
            setFormData((prev) => {
                const questions = [...prev.caseQuestions];
                const ckObj = { ...questions[qIndex].calculationKeyPoints };
                ckObj[keyName] = keyValue;
                questions[qIndex].calculationKeyPoints = ckObj;
                return { ...prev, caseQuestions: questions };
            });
        }
    };

    const handleCalculationKeyPointRemove = (qIndex, keyToRemove) => {
        setFormData((prev) => {
            const questions = [...prev.caseQuestions];
            const ckObj = { ...questions[qIndex].calculationKeyPoints };
            delete ckObj[keyToRemove];
            questions[qIndex].calculationKeyPoints = ckObj;
            return { ...prev, caseQuestions: questions };
        });
    };

    /* -------------------------------------------------------------------------
     * EDITING/DELETING CASE
     * ------------------------------------------------------------------------*/
    const handleEditCase = (caseId) => {
        const foundCase = cases.find((c) => c.id === caseId);
        if (!foundCase) return;

        // Convert arrays to Set where needed
        const newFormData = {
            ...foundCase,
            caseContext: {
                ...foundCase.caseContext,
                additionalInformation: new Set(
                    foundCase.caseContext.additionalInformation || []
                ),
            },
            caseQuestions: foundCase.caseQuestions.map((q) => ({
                ...q,
                basicInformation: new Set(q.basicInformation || []),
                additionalInformation: new Set(q.additionalInformation || []),
                calculationKeyPoints: q.calculationKeyPoints || {},
            })),
        };
        setFormData(newFormData);
        setCurrentCaseId(caseId);
        setIsEditing(true);
    };

    const handleDeleteCase = (caseId) => {
        if (window.confirm("Are you sure you want to delete this case?")) {
            setCases((prev) => prev.filter((c) => c.id !== caseId));
        }
    };

    /* -------------------------------------------------------------------------
     * SUBMIT FORM -> ADD/UPDATE CASE & (optionally) UPLOAD TO FIREBASE
     * ------------------------------------------------------------------------*/
    const handleSubmitCase = async (e) => {
        e.preventDefault();
        const newCaseId = isEditing ? currentCaseId : Date.now();

        // Prepare finalCase
        const finalCase = {
            ...formData,
            id: newCaseId,
            caseContext: {
                ...formData.caseContext,
                caseId: newCaseId,
                additionalInformation: Array.from(
                    formData.caseContext.additionalInformation || []
                ),
            },
            caseQuestions: formData.caseQuestions.map((q, idx) => ({
                ...q,
                caseId: newCaseId,
                questionId: q.questionId || idx + 1,
                basicInformation: Array.from(q.basicInformation || []),
                additionalInformation: Array.from(
                    q.additionalInformation || []
                ),
            })),
        };

        if (isEditing) {
            setCases((prev) =>
                prev.map((c) => (c.id === currentCaseId ? finalCase : c))
            );
        } else {
            setCases((prev) => [...prev, finalCase]);
        }

        // Example: Upload to Firebase (pseudo)
        try {
            console.log("TODO: Uploading case to Firebase...");
            // const response = await fetch("https://<REGION>-<PROJECT_ID>.cloudfunctions.net/uploadCaseProblems", {
            //   method: "POST",
            //   headers: { "Content-Type": "application/json" },
            //   body: JSON.stringify([finalCase]),
            // });
            // const data = await response.json();
            // console.log("Firebase upload response:", data);
        } catch (err) {
            console.error("Error uploading case to Firebase:", err);
        }

        // Reset form
        setFormData(getInitialCaseState());
        setIsEditing(false);
        setCurrentCaseId(null);
    };

    /* -------------------------------------------------------------------------
     * RENDER
     * ------------------------------------------------------------------------*/
    return (
        <div className="p-6 max-w-6xl mx-auto">
            {/* CASE FORM */}
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>
                        {isEditing ? "Edit Case" : "Add New Case"}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmitCase} className="space-y-4">
                        {/* Top row fields */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Title
                                </label>
                                <Input
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Expected Time (minutes)
                                </label>
                                <Input
                                    name="expectedTime"
                                    value={formData.expectedTime}
                                    onChange={handleInputChange}
                                    placeholder="30-45"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Interviewer Led
                                </label>
                                <Switch
                                    checked={formData.isInterviewerLed}
                                    onCheckedChange={handleInterviewerLedChange}
                                />
                            </div>
                        </div>

                        {/* Industry Selection */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium">
                                Industry
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {Object.values(Industry).map((industry) => (
                                    <Button
                                        key={industry}
                                        type="button"
                                        variant={
                                            formData.industry === industry
                                                ? "default"
                                                : "outline"
                                        }
                                        onClick={() =>
                                            handleIndustrySelect(industry)
                                        }
                                        className="capitalize">
                                        {industry
                                            .toLowerCase()
                                            .replace("_", " ")}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        {/* Case Type Selection */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium">
                                Case Type
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {Object.values(CaseType).map((type) => (
                                    <Button
                                        key={type}
                                        type="button"
                                        variant={
                                            formData.caseType === type
                                                ? "default"
                                                : "outline"
                                        }
                                        onClick={() =>
                                            handleCaseTypeSelect(type)
                                        }
                                        className="capitalize">
                                        {type.toLowerCase().replace("_", " ")}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        {/* Tags */}
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Tags (Press Enter to add)
                            </label>
                            <Input
                                onKeyPress={handleTagInput}
                                placeholder="Add tag and press Enter"
                            />
                            <div className="flex flex-wrap gap-2 mt-2">
                                {formData.tags.map((tag) => (
                                    <span
                                        key={tag}
                                        className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-sm">
                                        {tag}
                                        <X
                                            className="w-3 h-3 cursor-pointer"
                                            onClick={() => removeTag(tag)}
                                        />
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Companies */}
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Companies (Press Enter to add)
                            </label>
                            <Input
                                onKeyPress={handleCompanyInput}
                                placeholder="Add company and press Enter"
                            />
                            <div className="flex flex-wrap gap-2 mt-2">
                                {formData.companies.map((company) => (
                                    <span
                                        key={company}
                                        className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-sm">
                                        {company}
                                        <X
                                            className="w-3 h-3 cursor-pointer"
                                            onClick={() =>
                                                removeCompany(company)
                                            }
                                        />
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Graph URL (CaseContext) */}
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Graph URL
                            </label>
                            <Input
                                name="graph"
                                value={formData.caseContext.graph || ""}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        caseContext: {
                                            ...prev.caseContext,
                                            graph: e.target.value,
                                        },
                                    }))
                                }
                                placeholder="Optional: https://example.com/my-graph.png"
                            />
                        </div>

                        {/* Case Key Points */}
                        <CaseKeyPoints
                            keyPoints={formData.keyPoints}
                            handleAddCaseKeyPoint={handleAddCaseKeyPoint}
                            handleRemoveCaseKeyPoint={handleRemoveCaseKeyPoint}
                            handleCaseKeyPointChange={handleCaseKeyPointChange}
                            handleCaseDataPointInput={handleCaseDataPointInput}
                            removeCaseDataPoint={removeCaseDataPoint}
                        />

                        {/* Case Questions */}
                        <CaseQuestions
                            caseQuestions={formData.caseQuestions}
                            handleAddQuestion={handleAddQuestion}
                            handleRemoveQuestion={handleRemoveQuestion}
                            handleQuestionChange={handleQuestionChange}
                            handleQuestionBasicInfo={handleQuestionBasicInfo}
                            removeQuestionBasicInfo={removeQuestionBasicInfo}
                            handleQuestionAdditionalInfo={
                                handleQuestionAdditionalInfo
                            }
                            removeQuestionAdditionalInfo={
                                removeQuestionAdditionalInfo
                            }
                            handleAddQuestionKeyPoint={
                                handleAddQuestionKeyPoint
                            }
                            handleRemoveQuestionKeyPoint={
                                handleRemoveQuestionKeyPoint
                            }
                            handleQuestionKeyPointChange={
                                handleQuestionKeyPointChange
                            }
                            handleQuestionKeyPointDataInput={
                                handleQuestionKeyPointDataInput
                            }
                            removeQuestionKeyPointDataPoint={
                                removeQuestionKeyPointDataPoint
                            }
                            handleCalculationKeyPointAdd={
                                handleCalculationKeyPointAdd
                            }
                            handleCalculationKeyPointRemove={
                                handleCalculationKeyPointRemove
                            }
                        />

                        {/* SUBMIT & BACK BUTTONS */}
                        <div className="mt-6 flex space-x-2">
                            <Button type="submit">
                                {isEditing ? "Update Case" : "Add Case"}
                            </Button>
                            <Link to="/cases">
                                <Button variant="destructive">Back</Button>
                            </Link>
                        </div>
                    </form>
                </CardContent>
            </Card>

            {/* CASES LIST */}
            <CaseList
                cases={cases}
                handleEditCase={handleEditCase}
                handleDeleteCase={handleDeleteCase}
            />
        </div>
    );
};

export default CaseManagement;
